import { useEffect, useRef, type ReactNode } from "react";
import { useMsal, useIsAuthenticated, MsalAuthenticationTemplate } from "@azure/msal-react";
import { InteractionType, InteractionStatus } from "@azure/msal-browser";
import { loginRequest, isMsalConfigured } from "@/config/auth";
import { appConfig } from "@/config/app";
import { Spinner } from "@/components/reusable/Spinner";
import { trackEvent } from "@/services/telemetry";

interface AuthGateProps {
  children: ReactNode;
}

/**
 * Authentication gate that wraps the entire app.
 *
 * Behaviour by mode:
 *   - VITE_DEV_AUTH_BYPASS=true:  renders children immediately, no MSAL interaction.
 *   - MSAL not configured:        renders a config error screen.
 *   - User not authenticated:     triggers loginRedirect automatically.
 *   - User authenticated:         renders children.
 */
export function AuthGate({ children }: AuthGateProps) {
  if (appConfig.flags.devAuthBypass) {
    return <>{children}</>;
  }

  if (!isMsalConfigured) {
    return <MsalConfigError />;
  }

  return <MsalGate>{children}</MsalGate>;
}

/**
 * MSAL authentication gate. Uses MsalAuthenticationTemplate for the
 * happy path (auto-redirect), with explicit handling for the loading
 * and error states.
 */
function MsalGate({ children }: { children: ReactNode }) {
  const { inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const tracked = useRef(false);

  useEffect(() => {
    if (isAuthenticated && !tracked.current) {
      trackEvent("AuthGate.Authenticated");
      tracked.current = true;
    }
  }, [isAuthenticated]);

  // MSAL is processing a redirect/popup -- show loading
  if (inProgress !== InteractionStatus.None) {
    return <AuthLoading message="Signing in..." />;
  }

  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={loginRequest}
      loadingComponent={() => <AuthLoading message="Signing in..." />}
      errorComponent={MsalError}
    >
      {children}
    </MsalAuthenticationTemplate>
  );
}

function AuthLoading({ message }: { message: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-bg-primary)]">
      <div className="flex flex-col items-center gap-4">
        <Spinner size="lg" />
        <p className="text-sm text-[var(--color-text-secondary)]">{message}</p>
      </div>
    </div>
  );
}

function MsalError({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : "An authentication error occurred";

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-bg-primary)]">
      <div className="mx-auto max-w-md rounded-lg border border-red-200 bg-red-50 p-8 text-center dark:border-red-900 dark:bg-red-950">
        <h2 className="mb-2 text-lg font-semibold text-red-800 dark:text-red-200">
          Authentication Failed
        </h2>
        <p className="mb-4 text-sm text-red-700 dark:text-red-300">{message}</p>
        <button
          onClick={() => window.location.reload()}
          className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
        >
          Try Again
        </button>
      </div>
    </div>
  );
}

function MsalConfigError() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-bg-primary)]">
      <div className="mx-auto max-w-lg rounded-lg border border-amber-200 bg-amber-50 p-8 dark:border-amber-900 dark:bg-amber-950">
        <h2 className="mb-2 text-lg font-semibold text-amber-800 dark:text-amber-200">
          MSAL Not Configured
        </h2>
        <p className="mb-4 text-sm text-amber-700 dark:text-amber-300">
          Authentication is enabled but MSAL credentials are missing. Set these environment
          variables in your <code>.env.development</code> file:
        </p>
        <pre className="rounded-md bg-amber-100 p-4 text-xs text-amber-900 dark:bg-amber-900 dark:text-amber-100">
          {`VITE_MSAL_CLIENT_ID=your-client-id
VITE_MSAL_AUTHORITY=https://login.microsoftonline.com/your-tenant-id
VITE_MSAL_REDIRECT_URI=http://localhost:3000`}
        </pre>
        <p className="mt-4 text-sm text-amber-700 dark:text-amber-300">
          Or set <code>VITE_DEV_AUTH_BYPASS=true</code> to skip authentication during development.
        </p>
      </div>
    </div>
  );
}
